import {useEffect, useMemo, useState} from 'react';
import {AxiosError} from 'axios';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  GetAppointmentDetailsParams,
  GetAppointmentListParams,
  GetBookingSlotParams,
  PatchConfirmBookingErrorResponseData,
  PatchConfirmBookingPayload,
  PatchRescheduleBookingPayload,
  getAppointmentDetails,
  getAppointmentList,
  getBookingSlots,
  getFilterOptions,
  patchConfirmBooking,
  patchRescheduleBooking,
  searchPricingLists,
  PricingListItem,
  addPaymentItem,
  AddPaymentItemPayload,
  addInsuranceClaimItem,
  AddInsuranceClaimItemPayload,
} from '$api/frontdesk';
import {downloadDashboardReport} from '$api/finance/dashboard';
import toast from '$utils/toast';
import {ToastType} from '$components/Toast/constant';
import {
  networkErrorToast,
  technicalGlitchToast,
} from '$constants/toastmessages';
import {useNavigate, useParams} from 'react-router-dom';
import {RouteSection, getRoute} from '$utils/route';
import {RoutePath} from '$constants/routes';
import {GetConsultantParams, getConsultants} from '$api/bookingFlow';
import {getPricingDetail} from '$api/frontdesk';
import moment from '$utils/date';

export function useConfirmBooking(showSuccessDialog: () => void) {
  const [disableSubmit, setDisableSubmit] = useState(false);
  const queryClient = useQueryClient();

  const {isPending, mutate} = useMutation({
    mutationKey: ['booking-confirm'],
    mutationFn: patchConfirmBooking,
    onSuccess: () => {
      showSuccessDialog();
    },
    onError: error => {
      if (error instanceof AxiosError) {
        const axiosError =
          error as AxiosError<PatchConfirmBookingErrorResponseData>;
        const response = axiosError.response;

        if (response) {
          if (response.status === 500) {
            return technicalGlitchToast();
          }

          if (response.data) {
            const data = response.data;
            return toast(ToastType.Error, 'Confirm booking', data.message);
          }
        }

        if (axiosError.message === 'Network Error') {
          return networkErrorToast();
        }

        return technicalGlitchToast();
      } else {
        technicalGlitchToast();
      }
    },
    onSettled: () => setDisableSubmit(false),
  });

  const sendConfirmBookingRequest = (payload: PatchConfirmBookingPayload) => {
    setDisableSubmit(true);
    mutate(payload);
  };

  return {
    isPending,
    disableSubmit,
    sendConfirmBookingRequest,
    queryClient,
  };
}

export function useRescheduleBooking() {
  const navigate = useNavigate();
  const params = useParams();
  const appointmentId = Number(params.id);
  const queryClient = useQueryClient();

  const [disableSubmit, setDisableSubmit] = useState(false);

  const {isPending, mutate} = useMutation({
    mutationKey: ['booking-reschedule'],
    mutationFn: patchRescheduleBooking,
    onSuccess: data => {
      navigate(
        getRoute(
          RouteSection.Calendar,
          RoutePath.staff.calendar.bookingDetails,
          appointmentId
        )
      );

      queryClient.invalidateQueries({
        queryKey: ['appointment-detail'],
      });

      return toast(ToastType.Success, 'Reschedule booking', data.message);
    },
    onError: error => {
      if (error instanceof AxiosError) {
        const axiosError =
          error as AxiosError<PatchConfirmBookingErrorResponseData>;
        const response = axiosError.response;

        if (response) {
          if (response.status === 500) {
            return technicalGlitchToast();
          }

          if (response.data) {
            const data = response.data;
            return toast(ToastType.Error, 'Reschedule booking', data.message);
          }
        }

        if (axiosError.message === 'Network Error') {
          return networkErrorToast();
        }

        return technicalGlitchToast();
      } else {
        technicalGlitchToast();
      }
    },
    onSettled: () => setDisableSubmit(false),
  });

  const sendRescheduleBookingRequest = (
    payload: PatchRescheduleBookingPayload
  ) => {
    setDisableSubmit(true);
    mutate(payload);
  };

  return {
    isPending,
    disableSubmit,
    sendRescheduleBookingRequest,
  };
}

export function useBookingSlots(params: GetBookingSlotParams) {
  const {
    date,
    hospitalId,
    doctorId,
    preferredTime,
    appointmentTypeId,
    subCareTypeId,
  } = params;

  const {data, isLoading, isRefetching, isError} = useQuery({
    queryKey: [
      'booking-slots',
      date,
      hospitalId,
      doctorId,
      preferredTime,
      appointmentTypeId,
      subCareTypeId,
    ],
    queryFn: () => getBookingSlots(params),
  });

  return {
    slots: data?.slots || [],
    slotsStartTime: data?.slotsStartTime,
    slotsEndTime: data?.slotsEndTime,
    isLoading: isLoading || isRefetching,
    isError: isError,
  };
}

export interface Consultant {
  id: number;
  fullName: string;
  profileUrl: string | null;
  doctorDetails: {
    bioLink: string;
    followupFee: number;
    consultationFee: number;
    specializations: {
      id: number;
      title: string;
    }[];
    description: string;
  } | null;
  priceLists: {
    followupFee: number;
    consultationFee: number;
  } | null;
}

export function useConsultants(params?: GetConsultantParams) {
  const {data, isLoading, isError} = useQuery({
    queryKey: [
      'consultants',
      params?.appointmentTypeId,
      params?.subCareId,
      params?.withoutFee,
    ],
    queryFn: () =>
      getConsultants({
        subCareId: params?.subCareId,
        appointmentTypeId: params?.appointmentTypeId,
        withoutFee: params?.withoutFee,
      }),
  });

  const consultants: Consultant[] = useMemo(() => {
    if (!data?.data) return [];

    return data.data.map(con => ({
      id: con.id,
      fullName: con.full_name,
      profileUrl: con.profile_url,
      doctorDetails: con.doctor_details
        ? {
            bioLink: con.doctor_details.bio_link,
            followupFee: con.doctor_details.followup_fee,
            consultationFee: con.doctor_details.consultation_fee,
            specializations: con.doctor_details.specializations,
            description: con.doctor_details.description,
          }
        : null,
      priceLists: con.price_lists
        ? {
            followupFee: con.price_lists.followup_fee,
            consultationFee: con.price_lists.consultation_fee,
          }
        : null,
    }));
  }, [data]);

  return {isLoading, consultants, isError};
}

export function useAppointmentList(params: GetAppointmentListParams) {
  const {doctorId, appointmentType, status, fromDate, toDate, search} = params;

  const {data, isLoading, isError, isPending} = useQuery({
    queryKey: [
      'frontdesk-appointments',
      doctorId,
      appointmentType,
      status,
      fromDate,
      toDate,
      search,
    ],
    queryFn: () => getAppointmentList(params),
  });

  return {
    isLoading,
    isError,
    isPending,
    appointments: data?.appointments || [],
    totalPages: data?.totalPages || 0,
    totalRecords: data?.totalRecords || 0,
  };
}

export function useAppointmentDetails(params: GetAppointmentDetailsParams) {
  const {appointmentId} = params;

  const {data, isLoading} = useQuery({
    queryKey: ['frontdesk-appointments', appointmentId],
    queryFn: () => getAppointmentDetails(params),
  });

  return {isLoading, appointmentDetails: data};
}

export function useAppointmentFilterOptions() {
  const {data, isPending, isError} = useQuery({
    queryKey: ['filter-options'],
    queryFn: getFilterOptions,
  });

  const filterOptions = data?.data || {
    status: [],
    consultant: [],
    appointment_type: [],
  };

  return {filterOptions, isPending, isError};
}

export function useDebounce<T>(initialValue: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(initialValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(initialValue);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [initialValue, delay]);

  return debouncedValue;
}

interface UseSearchPricingListResult {
  data: PricingListItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

export const useSearchPricingList = (
  search?: string
): UseSearchPricingListResult => {
  const {data, isLoading, isError, error} = useQuery({
    queryKey: ['pricing-lists', search],
    queryFn: async () => {
      try {
        const response = await searchPricingLists(search);
        return response.data;
      } catch (err) {
        throw new Error(
          err instanceof Error ? err.message : 'Failed to fetch pricing lists'
        );
      }
    },
    enabled: Boolean(search),
  });

  return {
    data,
    isLoading,
    isError,
    error: error as Error | null,
  };
};

export const usePricingDetail = (id: number | null) => {
  return useQuery({
    queryKey: ['pricingDetail', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await getPricingDetail(id);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useAddPaymentItem = () => {
  const queryClient = useQueryClient();
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {mutate, isPending} = useMutation({
    mutationFn: (payload: AddPaymentItemPayload) => addPaymentItem(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['payment-details', variables.item.payment_id.toString()],
      });
      return toast(ToastType.Success, 'Add payment item', data.message);
    },
    onError: () => {
      technicalGlitchToast();
    },
    onSettled: () => setDisableSubmit(false),
  });

  const sendAddPaymentItemRequest = (payload: AddPaymentItemPayload) => {
    setDisableSubmit(true);
    mutate(payload);
  };

  return {
    isPending,
    disableSubmit,
    sendAddPaymentItemRequest,
  };
};

export const useAddInsuranceClaimItem = () => {
  const queryClient = useQueryClient();
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {mutate, isPending} = useMutation({
    mutationFn: (payload: AddInsuranceClaimItemPayload) =>
      addInsuranceClaimItem(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          'insurance-claim-details',
          variables.item.insurance_claim_id.toString(),
        ],
      });
      return toast(ToastType.Success, 'Add insurance claim item', data.message);
    },
    onError: () => {
      toast(ToastType.Error, 'Add insurance claim item', 'Failed to add item');
    },
    onSettled: () => setDisableSubmit(false),
  });

  const sendAddInsuranceClaimItemRequest = (
    payload: AddInsuranceClaimItemPayload
  ) => {
    setDisableSubmit(true);
    mutate(payload);
  };

  return {
    isPending,
    disableSubmit,
    sendAddInsuranceClaimItemRequest,
  };
};

interface UseDownloadReportProps {
  dateFrom: Date | null;
  dateTo: Date | null;
  selectedDataOptions: string[];
  dataOptionMapping: Record<string, string>;
}

export const useDownloadReport = ({
  dateFrom,
  dateTo,
  selectedDataOptions,
  dataOptionMapping,
}: UseDownloadReportProps) => {
  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);

  const validateDownloadParams = () => {
    if (!dateFrom || !dateTo || selectedDataOptions.length === 0) {
      toast(
        ToastType.Error,
        'Download report',
        'Please select date range and data options'
      );
      return false;
    }
    return true;
  };

  const downloadReport = async (format: 'xlsx' | 'pdf') => {
    if (!validateDownloadParams()) return;

    const isCSV = format === 'xlsx';
    const setIsDownloading = isCSV ? setIsDownloadingCSV : setIsDownloadingPDF;
    const reportType = isCSV ? 'CSV' : 'PDF';
    const fileExtension = isCSV ? 'xlsx' : 'pdf';

    setIsDownloading(true);

    try {
      const mappedDataOptions = selectedDataOptions.map(
        option => dataOptionMapping[option]
      );

      const reportBlob = await downloadDashboardReport({
        data_options: mappedDataOptions,
        from_date: moment(dateFrom).format('YYYY-MM-DD'),
        to_date: moment(dateTo).format('YYYY-MM-DD'),
        format,
      });

      const url = window.URL.createObjectURL(reportBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dashboard_report_${moment(dateFrom).format('DD-MM-YYYY')}_to_${moment(dateTo).format('DD-MM-YYYY')}.${fileExtension}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast(
        ToastType.Success,
        'Download report',
        `${reportType} report downloaded successfully`
      );
    } catch (error) {
      console.error(`Error downloading ${reportType} report:`, error);
      toast(
        ToastType.Error,
        'Download report',
        `Failed to download ${reportType} report`
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleGenerateCSV = () => downloadReport('xlsx');
  const handleGeneratePDF = () => downloadReport('pdf');

  return {
    isDownloadingCSV,
    isDownloadingPDF,
    handleGenerateCSV,
    handleGeneratePDF,
  };
};
